import { ArrowUpRight } from 'lucide-react'
import type { KeyboardEvent } from 'react'
import { INLINE_EDIT_SELECTOR } from '@/components/settings/inline-edit'
import {
  isHiddenInMobileEditor,
  type SettingsSectionSlug,
  settingsSections,
} from '@/components/settings/sections'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDevice } from '@/hooks/use-mobile'
import { useOpenSettingsSection } from '@/hooks/use-open-settings-section'
import { cn } from '@/lib/utils'

/** The settings sections the editor modal offers, as `/settings` slugs. */
export const EDITOR_SETTINGS_SLUGS = [
  'code-editor',
  'timer',
  'shortcuts',
  'postmortem',
  'layouts',
] as const satisfies readonly SettingsSectionSlug[]

export type EditorSettingsSlug = (typeof EDITOR_SETTINGS_SLUGS)[number]

type EditorSettingsSection = Extract<
  (typeof settingsSections)[number],
  { slug: EditorSettingsSlug }
>

/** The modal's sections, in `/settings` registry order so the two cannot drift. */
const editorSettingsSections = settingsSections.filter(
  (section): section is EditorSettingsSection =>
    (EDITOR_SETTINGS_SLUGS as readonly string[]).includes(section.slug),
)

/** The section a bare open, or a section missing on this device, falls back to. */
const DEFAULT_EDITOR_SETTINGS_SLUG: EditorSettingsSlug = 'code-editor'

const tabId = (slug: EditorSettingsSlug) => `editor-settings-tab-${slug}`
const panelId = (slug: EditorSettingsSlug) => `editor-settings-panel-${slug}`

/**
 * Quick-access settings over the editor. No backdrop, so a change (font size,
 * theme) shows live in the editor behind it. The bodies are the same
 * components `/settings` mounts; a body that finishes its job (applying a
 * layout) closes the modal through `onDone`. Sections marked
 * `hideInMobileEditor` are left out on mobile and tablet.
 */
export function EditorSettingsModal({
  slug,
  onSelect,
  onClose,
  returnFocusTo,
}: {
  /** The open section, or null when the modal is closed. */
  slug: EditorSettingsSlug | null
  onSelect: (slug: EditorSettingsSlug) => void
  onClose: () => void
  /**
   * Where focus goes back to on close: the control that opened the modal.
   * There is no DialogTrigger to fall back on, since every entry point opens
   * it programmatically.
   */
  returnFocusTo?: HTMLElement | null
}) {
  const { isMobile } = useDevice()
  const openSettingsSection = useOpenSettingsSection()

  const sections = isMobile
    ? editorSettingsSections.filter(
        (section) => !isHiddenInMobileEditor(section),
      )
    : editorSettingsSections
  const active =
    sections.find((section) => section.slug === slug) ??
    sections.find((section) => section.slug === DEFAULT_EDITOR_SETTINGS_SLUG)
  if (!active) throw new Error('The editor settings modal has no sections')

  // Roving focus along the menu: Up/Down on the desktop's vertical menu,
  // Left/Right on the mobile strip. Home/End jump to either end.
  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const [previousKey, nextKey] = isMobile
      ? ['ArrowLeft', 'ArrowRight']
      : ['ArrowUp', 'ArrowDown']
    const current = sections.findIndex((s) => s.slug === active?.slug)
    let next: number
    if (event.key === nextKey) next = (current + 1) % sections.length
    else if (event.key === previousKey)
      next = (current - 1 + sections.length) % sections.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = sections.length - 1
    else return
    event.preventDefault()
    const target = sections[next]
    if (!target) return
    onSelect(target.slug)
    document.getElementById(tabId(target.slug))?.focus()
  }

  const Body = active.Component

  return (
    <Dialog
      open={slug !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        showOverlay={false}
        onEscapeKeyDown={(event) => {
          if (
            event.target instanceof Element &&
            event.target.closest(INLINE_EDIT_SELECTOR)
          ) {
            event.preventDefault()
          }
        }}
        onCloseAutoFocus={(event) => {
          if (returnFocusTo?.isConnected) {
            event.preventDefault()
            returnFocusTo.focus()
          }
        }}
        className={cn(
          'flex h-[min(40rem,85vh)] w-[min(56rem,95vw)] max-w-none gap-0 overflow-hidden p-0 sm:max-w-none',
          isMobile ? 'flex-col' : 'flex-row',
        )}
      >
        <div
          className={cn(
            'flex shrink-0 flex-col',
            isMobile ? 'border-b' : 'w-60 border-r',
          )}
        >
          <div
            className={cn(isMobile ? 'px-4 pt-4 pb-2 pr-12' : 'px-4 pt-6 pb-4')}
          >
            <DialogTitle
              asChild
              className="text-2xl leading-tight font-bold tracking-tight"
            >
              <h1>Settings</h1>
            </DialogTitle>
            <DialogDescription className="mt-1">
              Manage your editor settings.
            </DialogDescription>
          </div>

          <div
            role="tablist"
            aria-label="Editor settings sections"
            aria-orientation={isMobile ? 'horizontal' : 'vertical'}
            className={cn(
              'flex gap-1 px-2 pb-2',
              isMobile ? 'overflow-x-auto' : 'flex-col',
            )}
          >
            {sections.map((section) => {
              const isActive = section.slug === active.slug
              return (
                <button
                  key={section.slug}
                  id={tabId(section.slug)}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={isActive ? panelId(section.slug) : undefined}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => onSelect(section.slug)}
                  onKeyDown={handleTabKeyDown}
                  className={cn(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm whitespace-nowrap transition-colors',
                    isActive
                      ? 'bg-accent text-accent-foreground font-medium'
                      : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                  )}
                >
                  <section.Icon className="h-4 w-4 shrink-0" aria-hidden />
                  {section.label}
                </button>
              )
            })}
          </div>
        </div>

        <section
          id={panelId(active.slug)}
          role="tabpanel"
          aria-labelledby={tabId(active.slug)}
          className="flex min-h-0 min-w-0 flex-1 flex-col"
        >
          <h2 className="border-b px-6 py-4 pr-12 text-lg font-semibold">
            {active.label}
          </h2>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            <Body onDone={onClose} />
          </div>
          <div className="flex justify-end border-t px-6 py-2">
            <Button
              type="button"
              variant="link"
              size="sm"
              // Same tab, as LG-1115 decided; LG-1204 adds a warning before
              // leaving the editor with work in progress.
              onClick={() => {
                onClose()
                openSettingsSection(active.slug)
              }}
            >
              Open full settings
              <ArrowUpRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </section>
      </DialogContent>
    </Dialog>
  )
}
